import { useCallback, useMemo, useState, useEffect } from "react";
import type { Side } from "@design-lab/shared";
import type { ID } from "common/utils/models";
import { trackCopyArtworkClicked } from "@design-lab/shared/lib/analytics";
import { useArtwork } from "./useArtwork";
import { useArtworkForm } from "./useArtworkForm";
import { setArtworkId } from "../lib/artworkFormHelpers";

type ArtworkMachine = NonNullable<ReturnType<ReturnType<typeof useArtwork>["getMachineBySide"]>>;

export function useArtworkCopy(currentSide: Side | undefined) {
  const { getMachine, getMachineBySide } = useArtwork();
  const { form } = useArtworkForm();
  const [isShowingCopySuccess, setIsShowingCopySuccess] = useState(false);

  const oppositeSide: Side | undefined = useMemo(() => {
    if (currentSide === "front") return "back";
    if (currentSide === "back") return "front";
    return undefined;
  }, [currentSide]);

  const currentMachine = currentSide ? getMachineBySide(currentSide) : undefined;
  const oppositeMachine = oppositeSide ? getMachineBySide(oppositeSide) : undefined;

  const hasArtworkOnCurrentSide = useMemo(
    () => Boolean(currentMachine?.state.context.artworkId),
    [currentMachine?.state.context.artworkId]
  );

  const hasArtworkOnOppositeSide = useMemo(
    () => Boolean(oppositeMachine?.state.context.artworkId),
    [oppositeMachine?.state.context.artworkId]
  );

  const shouldShowCopyButton = useMemo(
    () => Boolean(currentSide && hasArtworkOnCurrentSide),
    [currentSide, hasArtworkOnCurrentSide]
  );

  useEffect(() => {
    if (isShowingCopySuccess) {
      const timeout = setTimeout(() => {
        setIsShowingCopySuccess(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isShowingCopySuccess]);

  /**
   * Generic method to copy artwork from one location to another.
   * Can copy by ID or by Side.
   */
  const copyArtwork = useCallback(
    (from: ID | Side, to: ID | Side) => {
      // Find the source machine
      let sourceMachine: ArtworkMachine | undefined;
      if (from === "front" || from === "back") {
        sourceMachine = getMachineBySide(from);
      } else {
        sourceMachine = getMachine(from);
      }

      if (!sourceMachine) {
        throw new Error(`Source machine not found for ${from}`);
      }

      // Find the target machine
      let targetMachine: ArtworkMachine | undefined;
      if (to === "front" || to === "back") {
        targetMachine = getMachineBySide(to);
      } else {
        targetMachine = getMachine(to);
      }

      if (!targetMachine) {
        throw new Error(`Target machine not found for ${to}`);
      }

      const sourceContext = sourceMachine.state.context;

      // Prepare the context to copy (excluding side)
      // Note: shouldRedirect is set to false to prevent navigation when copying,
      // as the target machine's side differs from the source and would navigate
      // to the wrong side. Normal uploads preserve their shouldRedirect behavior.
      const contextToCopy = {
        shouldRedirect: false,
        // Prevent UI auto-switch triggered by SideSwitcher while copied side starts processing
        suppressAutoSwitch: true,
        file: sourceContext.file,
        artworkId: sourceContext.artworkId,
        processTimeStamp: sourceContext.processTimeStamp,
        originalImageUrl: sourceContext.originalImageUrl,
        artwork: sourceContext.artwork,
        startTime: sourceContext.startTime,
        source: sourceContext.source,
        lastWebsocketEvent: sourceContext.lastWebsocketEvent,
        cacheWarmingStatus: sourceContext.cacheWarmingStatus,
        completionTriggerEvent: sourceContext.completionTriggerEvent,
        visitedStates: sourceContext.visitedStates
      };

      // Reset the target machine with the copied context
      const targetState = sourceMachine.state.matches("finished") ? "finished" : "initial";

      targetMachine.send({
        type: "reset",
        context: contextToCopy,
        targetState
      });

      // Handle different source states
      if (sourceMachine.state.matches({ processing: "progress" })) {
        // If source is uploading, start upload on target
        if (sourceContext.file && sourceContext.source && sourceContext.processTimeStamp) {
          targetMachine.send({
            type: "upload.started",
            file: sourceContext.file,
            source: sourceContext.source,
            processTimeStamp: sourceContext.processTimeStamp
          });
        }
      } else if (sourceMachine.state.matches({ processing: "uploaded" })) {
        // If source is uploaded but not enhancing yet
        if (sourceContext.file && sourceContext.source && sourceContext.processTimeStamp) {
          targetMachine.send({
            type: "upload.started",
            file: sourceContext.file,
            source: sourceContext.source,
            processTimeStamp: sourceContext.processTimeStamp
          });
          if (sourceContext.artworkId) {
            targetMachine.send({
              type: "upload.completed",
              id: sourceContext.artworkId
            });
          }
        }
      } else if (
        sourceMachine.state.matches({ processing: "enhancing" }) ||
        sourceMachine.state.matches({ processing: "warming" })
      ) {
        // If source is enhancing or warming cache
        if (sourceContext.artworkId) {
          targetMachine.send({ type: "enhancement.started" });
          if (sourceMachine.state.matches({ processing: "warming" })) {
            targetMachine.send({ type: "cache.warming.started" });
            if (sourceContext.cacheWarmingStatus === "warmed") {
              targetMachine.send({ type: "cache.warmed" });
            }
          }
        }
      } else if (sourceMachine.state.matches("finished")) {
        // Source is finished, nothing extra needed as reset handles it
      }
    },
    [getMachine, getMachineBySide]
  );

  /**
   * Specific method to copy artwork from the current side to the opposite side.
   * Handles analytics tracking, form updates, and error handling.
   */
  const copyArtworkToOtherSide = useCallback(() => {
    if (!currentSide) {
      throw new Error("Current side is not defined");
    }

    if (!oppositeSide) {
      throw new Error("Opposite side is not defined");
    }

    const sourceMachine = getMachineBySide(currentSide);
    const { artworkId } = sourceMachine.state.context;

    if (!artworkId) {
      throw new Error("No artwork ID to copy");
    }

    // Track analytics event
    trackCopyArtworkClicked(currentSide, oppositeSide);

    // Copy state machine data from current side to opposite side
    copyArtwork(currentSide, oppositeSide);

    // Update the form with the artwork ID for the opposite side
    setArtworkId(form, oppositeSide, String(artworkId));

    // Set success feedback state to show copy confirmation
    setIsShowingCopySuccess(true);
  }, [currentSide, oppositeSide, copyArtwork, getMachineBySide, form]);

  return {
    copyArtwork,
    copyArtworkToOtherSide,
    currentSide,
    oppositeSide,
    hasArtworkOnCurrentSide,
    hasArtworkOnOppositeSide,
    shouldShowCopyButton,
    isShowingCopySuccess
  };
}
